import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { PageNode, PageElement } from '@holloway/dsl';
import { createDefaultPageElement } from '@/features/editor/pageElements';

interface UsePageEditorStateProps {
  pageNode: Partial<PageNode>;
  opened: boolean;
  onSave: (updatedNode: Partial<PageNode>) => void;
  onClose: () => void;
  onSaveFlow?: () => void;
}

export function usePageEditorState({
  pageNode,
  opened,
  onSave,
  onClose,
  onSaveFlow,
}: UsePageEditorStateProps) {
  const [editingNode, setEditingNode] = useState<Partial<PageNode>>(pageNode);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const activePageIdRef = useRef<string | undefined>(undefined);
  const wasOpenedRef = useRef(false);

  // Reset state only when the modal starts editing a page.
  useEffect(() => {
    if (!opened) {
      activePageIdRef.current = undefined;
      wasOpenedRef.current = false;
      return;
    }

    const justOpened = !wasOpenedRef.current;
    if (justOpened || activePageIdRef.current !== pageNode.id) {
      activePageIdRef.current = pageNode.id;
      setEditingNode(pageNode);
      setSelectedElementId(null);
    }
    wasOpenedRef.current = true;
  }, [opened, pageNode]);

  const handleAddElement = (type: string) => {
    // Count existing elements of the same type to generate sequential numbers
    const existingElements = editingNode.elements || [];
    const sameTypeCount = existingElements.filter(
      (el) => el.type === type
    ).length;
    const elementNumber = sameTypeCount + 1;
    
    // Generate a short unique ID
    const shortId = Math.random().toString(36).substring(2, 8);
    const id = `${type.toLowerCase()}-${shortId}`;
    
    const newElement = createDefaultPageElement(type, id, elementNumber) as PageElement;

    setEditingNode((prev) => ({
      ...prev,
      elements: [...(prev.elements || []), newElement],
    }));
  };

  const handleRemoveElement = (id: string) => {
    setEditingNode((prev) => ({
      ...prev,
      elements: (prev.elements || []).filter((el) => el.id !== id),
    }));
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  };

  const handleUpdateElement = (id: string, updates: Partial<PageElement>) => {
    setEditingNode((prev) => ({
      ...prev,
      elements: (prev.elements || []).map((el) =>
        el.id === id ? { ...el, ...updates } as PageElement : el
      ),
    }));
  };

  const handleReorderElements = (elements: PageElement[]) => {
    setEditingNode((prev) => ({
      ...prev,
      elements,
    }));
  };

  const validateBranchingElements = (): string | null => {
    const branchingElements = (editingNode.elements || []).filter(
      (el) => (el as any).createBranches === true
    );

    if (branchingElements.length > 1) {
      return 'Only one element can have branching enabled per page. Please disable branching on other elements.';
    }

    return null;
  };

  const handleSave = () => {
    const validationError = validateBranchingElements();
    if (validationError) {
      alert(validationError);
      return;
    }

    flushSync(() => {
      onSave(editingNode);
    });
    if (onSaveFlow) onSaveFlow();
    onClose();
  };

  const handleApply = () => {
    const validationError = validateBranchingElements();
    if (validationError) {
      alert(validationError);
      return;
    }

    flushSync(() => {
      onSave(editingNode);
    });
    if (onSaveFlow) onSaveFlow();
  };

  const selectedElement = editingNode.elements?.find((el) => el.id === selectedElementId);

  return {
    editingNode,
    setEditingNode,
    selectedElementId,
    setSelectedElementId,
    selectedElement,
    handleAddElement,
    handleRemoveElement,
    handleUpdateElement,
    handleReorderElements,
    handleSave,
    handleApply,
  };
}
